/**
 * Part 13 — sanitise OMS → Xero export content.
 *
 * Accounting records must not receive:
 * - debugging dumps, credentials/tokens
 * - irrelevant internal IDs
 * - unrelated personal / cross-venue / cross-supplier data
 *
 * OMS deep links stay path-based (no embedded auth); opening them still
 * requires normal OMS login + authorisation.
 */

const MONGO_OBJECT_ID_RE = /^[a-f0-9]{24}$/i
const JWT_RE = /^eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const SECRET_QUERY_KEYS = new Set([
  'token',
  'access_token',
  'id_token',
  'refresh_token',
  'code',
  'state',
  'key',
  'secret',
  'password',
  'auth',
  'authorization',
  'apikey',
  'api_key',
  'session',
  'sid',
])

/** Safe invoice/receipting query keys allowed on OMS deep links. */
const SAFE_OMS_QUERY_KEYS = new Set([
  'q',
  'po',
  'filter',
  'matching',
  'upload',
  'supplier',
  'supplierId',
])

export function isMongoObjectId(value) {
  return MONGO_OBJECT_ID_RE.test(String(value || '').trim())
}

export function looksLikeCredentialOrToken(value) {
  const s = String(value || '').trim()
  if (!s) return false
  if (JWT_RE.test(s)) return true
  if (/^bearer\s+/i.test(s)) return true
  if (/access[_-]?token|refresh[_-]?token|id[_-]?token/i.test(s)) return true
  if (/^(sk|pk|rk)[_-]live[_-]/i.test(s)) return true
  if (s.length > 80 && /[A-Za-z0-9+/=_-]{40,}/.test(s)) return true
  return false
}

/**
 * Display names for Ordered/Approved/Receipted/Reconciled by.
 * Keep workplace names; drop emails, ObjectIds, and tokens.
 */
export function sanitizePersonDisplayName(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return null
  if (looksLikeCredentialOrToken(raw)) return null
  if (isMongoObjectId(raw)) return null

  if (EMAIL_RE.test(raw)) {
    // Do not put email addresses into customer accounting records.
    return null
  }

  // Reject strings that embed emails or tokens mid-text.
  if (/[^\s@]+@[^\s@]+\.[^\s@]+/.test(raw)) return null
  if (looksLikeCredentialOrToken(raw)) return null

  // Cap length — names, not dumps.
  return raw.slice(0, 80)
}

/**
 * Ensure OMS Url is https/http, no credentials, only safe query keys.
 * Never embeds tokens — OMS auth is required when the user opens the link.
 */
export function sanitizeOmsDeepLinkUrl(url, { allowedOrigins = [] } = {}) {
  const raw = String(url || '').trim()
  if (!raw) return null

  let parsed
  try {
    parsed = new URL(raw)
  } catch {
    return null
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null
  if (parsed.username || parsed.password) return null
  if (looksLikeCredentialOrToken(parsed.hash.replace(/^#/, ''))) {
    parsed.hash = ''
  } else {
    // Drop opaque hashes — not needed for OMS deep links.
    parsed.hash = ''
  }

  const allowed = (Array.isArray(allowedOrigins) ? allowedOrigins : [])
    .map(o => String(o || '').trim().replace(/\/$/, '').toLowerCase())
    .filter(Boolean)
  if (allowed.length) {
    const origin = parsed.origin.toLowerCase()
    if (!allowed.includes(origin)) return null
  }

  const nextParams = new URLSearchParams()
  for (const [key, value] of parsed.searchParams.entries()) {
    const k = String(key).toLowerCase()
    if (SECRET_QUERY_KEYS.has(k)) continue
    if (!SAFE_OMS_QUERY_KEYS.has(k)) continue
    if (looksLikeCredentialOrToken(value)) continue
    if (isMongoObjectId(value) && k !== 'supplierid') continue
    nextParams.set(key, value)
  }
  parsed.search = nextParams.toString() ? `?${nextParams.toString()}` : ''

  // Path may include venue/invoice ids for routing; those are not secrets and
  // still require OMS session + venue authorisation when opened.
  return parsed.toString()
}

/**
 * Scrub free-text (History note / audit PDF) before it leaves OMS.
 */
export function sanitizeTextForXeroAccounting(text) {
  const raw = String(text || '')
  if (!raw) return ''

  return raw
    .split(/\r?\n/)
    .map(line => {
      let next = line
      if (looksLikeCredentialOrToken(next)) return null
      // Redact emails
      next = next.replace(
        /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi,
        '[redacted]',
      )
      // Redact standalone Mongo ObjectIds (keep PO/invoice numbers)
      next = next.replace(
        /(^|[^a-z0-9])([a-f0-9]{24})(?=[^a-z0-9]|$)/gi,
        (match, prefix) => `${prefix}[id]`,
      )
      // Drop obvious debug lines
      if (/^\s*(debug|stack|trace|sentry|console)\b/i.test(next)) return null
      if (/\b(accessToken|refreshToken|idToken|clientSecret)\b/i.test(next)) {
        return null
      }
      return next
    })
    .filter(line => line != null)
    .join('\n')
    .slice(0, 20000)
}

/**
 * Keep finance documents that belong to this venue / PO / supplier only.
 */
export function filterFinanceDocumentsForXeroAudit(
  documents = [],
  {
    venueId = null,
    purchaseOrderNumber = null,
    supplierId = null,
  } = {},
) {
  const venue = String(venueId || '').trim()
  const po = String(purchaseOrderNumber || '').trim()
  const supplier = String(supplierId || '').trim()

  return (Array.isArray(documents) ? documents : []).filter(doc => {
    if (!doc || doc.isArchived) return false

    if (venue) {
      const docVenue = String(
        doc?.venue?._id || doc?.venue || doc?.venueId || '',
      ).trim()
      if (docVenue && docVenue !== venue) return false
    }

    if (supplier) {
      const docSupplier = String(
        doc?.supplier?._id || doc?.supplier || doc?.supplierId || '',
      ).trim()
      if (docSupplier && docSupplier !== supplier) return false
    }

    if (po) {
      const allocations = Array.isArray(doc?.poAllocations)
        ? doc.poAllocations
        : []
      const linkedPos = [
        String(doc?.purchaseOrderNumber || '').trim(),
        ...allocations.map(a => String(a?.purchaseOrderNumber || '').trim()),
      ].filter(Boolean)
      // If the document declares PO linkage, require a match; unlinked docs
      // with no PO fields are excluded when a PO scope is set.
      if (linkedPos.length > 0 && !linkedPos.includes(po)) return false
      if (
        linkedPos.length === 0 &&
        !String(doc?.invoiceNumber || '').trim()
      ) {
        return false
      }
    }

    return true
  })
}
